import moment from 'moment';
import { apiClient, authClient } from '../../api';
import { getErrorMessage } from '../../errorHandlers';

const IS_LEGACY_AUTH = !process.env.VUE_APP_AUTHENTICATION_SERVICE_URL;
const JWT_TOKEN_COOKIE_NAME = 'jwt_access_token';

let authPromise = null;

function parseJwt(token) {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

function setCookie(name, value, days) {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2)
    return parts
      .pop()
      .split(';')
      .shift();
}

const state = {
  authorized: false,
  submitting: false,
  errorMessage: '',
  session: {
    expiresAt: '1970-01-01T09:00:00.000Z',
    roles: [],
  },
  currentUser: {
    id: 0,
  },
  jwt: {
    accessToken: '',
    expiresAt: '1970-01-01T09:00:00.000Z',
  },
  customer: null,
  useAuthHeader: false,
};

const getters = {
  isAuthExpired(state) {
    return IS_LEGACY_AUTH
      ? moment(state.session.expiresAt).isSameOrBefore()
      : moment(state.jwt.expiresAt).isSameOrBefore();
  },

  isAuthAdmin(state) {
    return IS_LEGACY_AUTH
      ? state.session && state.session.roles.some((r) => r === 'admin')
      : state.currentUser.roles.includes('insurance.admin');
  },

  isCompanyAgent(state) {
    return state.currentUser.roles.includes('insurance.insurance_company_agent');
  },
};

const mutations = {
  authorizationStart(state) {
    state.submitting = true;
  },

  authorizationSuccess(state, { session, currentUser }) {
    state.authorized = true;
    state.submitting = false;
    state.errorMessage = '';
    state.session = session || state.session;
    state.currentUser = currentUser || state.currentUser;
  },

  authorizationReject(state, e) {
    state.session = {
      expiresAt: '1970-01-01T09:00:00.000Z',
      roles: [],
    };
    state.jwt = {
      accessToken: '',
      expiresAt: '1970-01-01T09:00:00.000Z',
    };
    state.authorized = false;
    state.currentUser = {};
    state.submitting = false;

    if (!e) {
      return;
    }

    switch (e.status) {
      case 403:
      case 400:
        state.errorMessage = 'Fel e-postadress eller lösenord';
        break;
      default:
        state.errorMessage = e.message;
        break;
    }
  },

  setAccessToken(state, { accessToken, useAuthHeader }) {
    const { user, exp } = parseJwt(accessToken);
    state.jwt.expiresAt = new Date(exp * 1000).toISOString();
    state.jwt.accessToken = accessToken;
    state.currentUser = user;
    state.authorized = true;
    state.submitting = false;
    state.errorMessage = '';
    state.useAuthHeader = !!useAuthHeader;

    if (!useAuthHeader) {
      setCookie(JWT_TOKEN_COOKIE_NAME, accessToken);
    }
  },

  setIframeCustomer(state, customer) {
    state.customer = customer;
  },
};

const actions = {
  async legacyLogin({ commit }, { email, password }) {
    // perform login against deprecated edge 1.x system
    try {
      commit('authorizationStart');

      await apiClient.post('/api/session/edge', {
        identity: email,
        password: password,
      });

      const { data: session } = await apiClient.get('/api/session');
      const { data: currentUser } = await apiClient.get('/api/employees/current');

      commit('authorizationSuccess', { session, currentUser });
    } catch (e) {
      commit('authorizationReject', {
        message: getErrorMessage(e),
        status: e.response && e.response.status,
      });
    }
  },

  async logout({ commit }) {
    try {
      commit('authorizationStart');
      await apiClient.delete('/api/session');
      commit('authorizationReject');
    } catch (e) {
      commit('authorizationReject', e.response.data || e);
    } finally {
      if (!IS_LEGACY_AUTH) {
        try {
          await authClient.post(`/api/auth/logout`);
        } finally {
          setCookie(JWT_TOKEN_COOKIE_NAME, '');
        }
      }
    }
  },

  async refreshAuth({ dispatch, commit, state, getters }, options) {
    const { force } = options || {};
    const shouldRefresh = (getters.isAuthExpired || force) && !state.submitting;

    if (shouldRefresh) {
      try {
        if (IS_LEGACY_AUTH) {
          const { data } = await apiClient.put('/api/session');
          commit('authorizationSuccess', { session: { ...state.session, ...data } });
        } else {
          if (!state.jwt.accessToken && !force) {
            const accessToken = getCookie(JWT_TOKEN_COOKIE_NAME);
            if (accessToken) {
              commit('setAccessToken', { accessToken });
              return dispatch('refreshAuth', options);
            }
          }

          commit('authorizationStart');
          const { data } = await authClient.post(`/api/auth/refresh`);
          commit('setAccessToken', data);
        }
        await dispatch('fetchCurrentUser');
      } catch (e) {
        commit('authorizationReject', e);
      }
    }
    return state.authorized;
  },

  async refreshAuthWait({ dispatch }, options) {
    if (!(authPromise instanceof Promise)) {
      authPromise = dispatch('refreshAuth', options);
    }
    authPromise = await authPromise;
    return authPromise;
  },

  async fetchCurrentUser({ commit, state }) {
    // Check we have current user data. Fetch if needed
    if (!state.currentUser.id) {
      const { data: currentUser } = await apiClient.get('/api/employees/current');
      commit('authorizationSuccess', { currentUser });
    }
  },
};

export default {
  state,
  mutations,
  actions,
  getters,
};
